/**
 * Oasis tier upsell: 5% off at 2 items, 10% off at 3+ items.
 *
 * The discounts are Shopify AUTOMATIC discounts ("Buy 2, save 5%" and
 * "Buy 3+, save 10%"). Shopify applies and recalculates them in the cart and at
 * checkout on its own. This file is presentation only: if it fails to load,
 * customers still get the correct discount.
 *
 * Custom elements:
 *   <oasis-tier-progress>  progress bar (cart drawer + cart page)
 *   <oasis-tier-offer>     product page offer with quantity shortcuts
 *   <oasis-tier-recs>      "Complete your bundle" strip in the cart drawer
 * Plus a returning visitor / exit intent prompt (built on demand).
 */
import { CartAddEvent, ThemeEvents } from '@theme/events';

const CONFIG_EL = document.getElementById('OasisTierConfig');
const CFG = CONFIG_EL ? safeJSON(CONFIG_EL.textContent) : null;

/* ------------------------------------------------------------------ */
/* Shared state and helpers                                            */
/* ------------------------------------------------------------------ */

const OUR_TITLES = ['Buy 2, save 5%', 'Buy 3+, save 10%'];
const LS_KEY = 'oasis_tier_v1';
const RETURN_GAP_MS = 30 * 60 * 1000; // a new visit = 30 min since last page view
const DISMISS_COOLDOWN_MS = 48 * 60 * 60 * 1000;
const TAB_AWAY_MS = 45 * 1000;

const S = (CFG && CFG.strings) || {};

const store = {
  count: CFG ? Number(CFG.cartCount) || 0 : 0,
  cart: null,
  lastRatio: null,
  shownTier: null,
};

function safeJSON(text) {
  try {
    return JSON.parse(text);
  } catch (_) {
    return null;
  }
}

const tierFor = (n) => (n >= 3 ? 2 : n === 2 ? 1 : 0);
const pctFor = (tier) => [0, 5, 10][tier] || 0;
const capped = (n) => Math.max(0, Math.min(3, n));
const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function escapeHTML(value) {
  return String(value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
}

function formatMoney(cents) {
  const fmt = String(CFG.moneyFormat || '{{amount}}').replace(/<[^>]*>/g, '');
  const value = (Number(cents) || 0) / 100;
  const fixed = (n, decimals, thousands = ',', decimal = '.') => {
    const [int, frac] = n.toFixed(decimals).split('.');
    return int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (frac ? decimal + frac : '');
  };
  return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
    switch (key) {
      case 'amount_no_decimals':
        return fixed(value, 0);
      case 'amount_with_comma_separator':
        return fixed(value, 2, '.', ',');
      case 'amount_no_decimals_with_comma_separator':
        return fixed(value, 0, '.', ',');
      case 'amount_with_apostrophe_separator':
        return fixed(value, 2, "'", '.');
      case 'amount_with_space_separator':
        return fixed(value, 2, ' ', ',');
      default:
        return fixed(value, 2);
    }
  });
}

function ourSaving(cart) {
  if (!cart) return 0;
  return (cart.cart_level_discount_applications || [])
    .filter((d) => OUR_TITLES.includes(d.title))
    .reduce((sum, d) => sum + (Number(d.total_allocated_amount) || 0), 0);
}

function subLine() {
  const saving = ourSaving(store.cart);
  return saving > 0 ? S.saving.replace('[amount]', formatMoney(saving)) : S.note;
}

/** Swap text with a soft crossfade, only when it actually changes. */
function setText(el, text) {
  if (!el || text == null || el.textContent.trim() === text) return;
  el.textContent = text;
  if (reducedMotion()) return;
  el.classList.remove('oasis-swap');
  void el.offsetWidth;
  el.classList.add('oasis-swap');
}

function session(key, value) {
  try {
    if (value === undefined) return sessionStorage.getItem(key);
    sessionStorage.setItem(key, value);
  } catch (_) {
    return null;
  }
}

function readMemory() {
  try {
    return JSON.parse(localStorage.getItem(LS_KEY) || '{}') || {};
  } catch (_) {
    return {};
  }
}

function writeMemory(data) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(data));
  } catch (_) {
    /* storage blocked: prompts simply do not persist */
  }
}

/* ------------------------------------------------------------------ */
/* Cart sync                                                           */
/* ------------------------------------------------------------------ */

let refreshTimer = 0;
let refreshSeq = 0;

function scheduleRefresh(delay = 120) {
  clearTimeout(refreshTimer);
  refreshTimer = setTimeout(refreshCart, delay);
}

async function refreshCart() {
  const seq = ++refreshSeq;
  try {
    const res = await fetch(CFG.routes.cartJs, { headers: { Accept: 'application/json' }, cache: 'no-store' });
    if (!res.ok) return null;
    const cart = await res.json();
    if (seq !== refreshSeq) return cart; // a newer request is on its way
    applyCart(cart);
    return cart;
  } catch (_) {
    return null;
  }
}

function applyCart(cart) {
  store.cart = cart;
  store.count = Number(cart.item_count) || 0;
  document.querySelectorAll('oasis-tier-progress').forEach((el) => el.update?.());
  document.querySelectorAll('oasis-tier-offer').forEach((el) => el.update?.());
  document.querySelectorAll('oasis-tier-recs').forEach((el) => el.render?.());
  Prompt.refresh();
}

/* ------------------------------------------------------------------ */
/* <oasis-tier-progress>                                               */
/* ------------------------------------------------------------------ */

class OasisTierProgress extends HTMLElement {
  static observedAttributes = ['data-tier'];

  connectedCallback() {
    const fill = this.querySelector('[data-oasis-fill]');
    const target = Math.min(1, Number(this.dataset.count || 0) / 3);

    // When the theme re-renders the drawer it may swap this node for a new one.
    // Start the new bar where the old one was so the fill glides instead of jumping.
    if (fill && store.lastRatio !== null && store.lastRatio !== target && !reducedMotion()) {
      fill.style.setProperty('--oasis-p', String(store.lastRatio));
      void fill.offsetWidth;
      requestAnimationFrame(() => fill.style.setProperty('--oasis-p', target.toFixed(4)));
    }
    store.lastRatio = target;

    const tier = Number(this.dataset.tier || 0);
    if (store.shownTier !== null && tier > store.shownTier) this.celebrate();
    store.shownTier = tier;

    if (store.cart) this.update();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name !== 'data-tier' || oldValue === null || oldValue === newValue) return;
    if (Number(newValue) > Number(oldValue) && Number(newValue) > (store.shownTier ?? -1)) this.celebrate();
    store.shownTier = Number(newValue);
  }

  update() {
    const n = store.count;
    const c = capped(n);
    const ratio = c / 3;

    this.toggleAttribute('hidden', n === 0);
    this.dataset.count = String(n);
    if (this.dataset.tier !== String(tierFor(n))) this.dataset.tier = String(tierFor(n));

    setText(this.querySelector('[data-oasis-msg]'), S[`status_${c}`]);
    setText(this.querySelector('[data-oasis-sub]'), subLine());

    this.querySelector('[data-oasis-fill]')?.style.setProperty('--oasis-p', ratio.toFixed(4));
    this.querySelector('[data-oasis-track]')?.setAttribute('aria-valuenow', String(c));
    this.querySelectorAll('[data-oasis-stop]').forEach((stop) => {
      stop.classList.toggle('is-reached', n >= Number(stop.getAttribute('data-oasis-stop')));
    });
    store.lastRatio = ratio;
  }

  celebrate() {
    if (reducedMotion()) return;
    this.classList.remove('is-celebrating');
    void this.offsetWidth;
    this.classList.add('is-celebrating');
    clearTimeout(this._celebrateTimer);
    this._celebrateTimer = setTimeout(() => this.classList.remove('is-celebrating'), 1600);
  }
}

/* ------------------------------------------------------------------ */
/* <oasis-tier-offer> (product page)                                   */
/* ------------------------------------------------------------------ */

class OasisTierOffer extends HTMLElement {
  connectedCallback() {
    this.tiers = [...this.querySelectorAll('[data-oasis-qty]')];
    this.msg = this.querySelector('[data-oasis-msg]');
    this.onClick = (event) => {
      const button = event.target.closest('[data-oasis-qty]');
      if (button && !button.disabled) this.pick(Number(button.dataset.oasisQty));
    };
    this.onQuantity = () => this.update();

    this.addEventListener('click', this.onClick);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.onQuantity);
    document.addEventListener('input', this.onQuantity, true);
    document.addEventListener('focusout', this.onQuantity, true);
    document.addEventListener(ThemeEvents.variantUpdate, this.onQuantity);
    this.update();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.onQuantity);
    document.removeEventListener('input', this.onQuantity, true);
    document.removeEventListener('focusout', this.onQuantity, true);
    document.removeEventListener(ThemeEvents.variantUpdate, this.onQuantity);
  }

  get scope() {
    return this.closest('.shopify-section') || document;
  }

  /** The main product form quantity selector (not the sticky bar or quick add). */
  get selector() {
    const all = [...this.scope.querySelectorAll('quantity-selector-component')];
    return all.find((el) => !el.closest('sticky-add-to-cart-component, .sticky-add-to-cart, dialog')) || all[0] || null;
  }

  get quantity() {
    const input = this.selector?.querySelector('input[name="quantity"]');
    return Math.max(1, parseInt(input?.value || '1', 10) || 1);
  }

  pick(qty) {
    const selector = this.selector;
    const input = selector?.querySelector('input[name="quantity"]');
    if (!selector || !input || input.disabled) return;

    let target = qty;
    const max = typeof selector.getEffectiveMax === 'function' ? selector.getEffectiveMax() : null;
    if (max !== null && max !== undefined) target = Math.min(target, max);

    if (typeof selector.setValue === 'function') selector.setValue(String(target));
    else input.value = String(target);
    selector.updateButtonStates?.();
    selector.onQuantityChange?.();

    // A quiet nudge toward the next step: the Add to cart button.
    const addButton = this.scope.querySelector('add-to-cart-component button, button[name="add"]');
    if (addButton && !reducedMotion()) {
      addButton.classList.remove('oasis-nudge');
      void addButton.offsetWidth;
      addButton.classList.add('oasis-nudge');
      setTimeout(() => addButton.classList.remove('oasis-nudge'), 900);
    }
    this.update();
  }

  update() {
    const n = store.count;
    const qty = this.quantity;

    this.tiers.forEach((button) => {
      const q = Number(button.dataset.oasisQty);
      button.classList.toggle('is-unlocked', n >= q);
      button.setAttribute('aria-pressed', String(q === 2 ? qty === 2 : qty >= 3));
    });

    const current = tierFor(n);
    const projected = tierFor(n + qty);
    const text = projected > current ? S.projected.replace('[pct]', `${pctFor(projected)}%`) : S[`status_${capped(n)}`];
    setText(this.msg, text);
    this.dataset.count = String(n);
    this.dataset.projected = String(projected);
  }

  highlight() {
    this.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'center' });
    this.classList.remove('is-highlighted');
    void this.offsetWidth;
    this.classList.add('is-highlighted');
    setTimeout(() => this.classList.remove('is-highlighted'), 1800);
  }
}

/* ------------------------------------------------------------------ */
/* <oasis-tier-recs> ("Complete your bundle" in the cart drawer)       */
/* ------------------------------------------------------------------ */

const recsCache = new Map();

async function fetchRecs(productId) {
  if (recsCache.has(productId)) return recsCache.get(productId);
  const promise = fetch(`${CFG.routes.recs}?product_id=${encodeURIComponent(productId)}&limit=10&intent=related`, {
    headers: { Accept: 'application/json' },
  })
    .then((res) => (res.ok ? res.json() : { products: [] }))
    .then((data) => data.products || [])
    .catch(() => []);
  recsCache.set(productId, promise);
  return promise;
}

function sizedImage(url, width) {
  if (!url) return '';
  const src = url.startsWith('//') ? `https:${url}` : url;
  return `${src}${src.includes('?') ? '&' : '?'}width=${width}`;
}

class OasisTierRecs extends HTMLElement {
  connectedCallback() {
    this.html = '';
    this.addEventListener('click', this.onClick);
    // The theme morphs the drawer after every cart change, which empties this
    // element. Put the last good render straight back so nothing flickers.
    this.observer = new MutationObserver(() => {
      if (this.busy || !this.shouldShow()) return;
      if (!this.querySelector('.oasis-recs__inner') || this.hidden) this.paint();
    });
    this.observer.observe(this, { childList: true, attributes: true, attributeFilter: ['hidden'] });
    this.render();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.removeEventListener('click', this.onClick);
  }

  shouldShow() {
    return store.count >= 1 && store.count < 3;
  }

  paint() {
    this.busy = true;
    if (this.shouldShow() && this.html) {
      if (this.innerHTML !== this.html) this.innerHTML = this.html;
      if (this.hidden) this.hidden = false;
    } else if (!this.hidden) {
      this.hidden = true;
    }
    this.busy = false;
  }

  async render() {
    if (!this.shouldShow()) {
      this.paint();
      return;
    }
    const cart = store.cart || (await refreshCart());
    if (!cart || !cart.items?.length) return;

    const inCart = new Set(cart.items.map((item) => item.product_id));
    const anchor = cart.items[0].product_id; // most recently added line
    const products = (await fetchRecs(anchor))
      .filter((p) => p.available && !inCart.has(p.id) && !p.gift_card)
      .slice(0, 6);

    if (!products.length) {
      this.html = '';
      this.paint();
      return;
    }

    const sub = S[`recs_sub_${capped(store.count)}`] || '';
    const items = products
      .map((p) => {
        const variant = (p.variants || []).find((v) => v.available) || p.variants?.[0];
        const single = (p.variants || []).length === 1 && variant;
        const price = formatMoney(variant ? variant.price : p.price);
        const action = single
          ? `<button type="button" class="oasis-recs__add" data-oasis-add="${variant.id}">${escapeHTML(S.add)}</button>`
          : `<a class="oasis-recs__add oasis-recs__add--link" href="${escapeHTML(p.url)}">${escapeHTML(S.choose)}</a>`;
        return `<li class="oasis-recs__item">
          <a class="oasis-recs__media" href="${escapeHTML(p.url)}" tabindex="-1" aria-hidden="true">
            ${p.featured_image ? `<img src="${escapeHTML(sizedImage(p.featured_image, 240))}" alt="" width="120" height="120" loading="lazy">` : ''}
          </a>
          <a class="oasis-recs__title" href="${escapeHTML(p.url)}">${escapeHTML(p.title)}</a>
          <span class="oasis-recs__price">${escapeHTML(price)}</span>
          ${action}
        </li>`;
      })
      .join('');

    this.html = `<div class="oasis-recs__inner">
      <div class="oasis-recs__head">
        <p class="oasis-recs__title-main">${escapeHTML(S.recs_title)}</p>
        <p class="oasis-recs__sub">${escapeHTML(sub)}</p>
      </div>
      <ul class="oasis-recs__list" role="list">${items}</ul>
    </div>`;
    const firstPaint = !this.querySelector('.oasis-recs__inner');
    if (firstPaint && !reducedMotion()) {
      this.dataset.animate = '';
      setTimeout(() => delete this.dataset.animate, 600);
    }
    this.paint();
  }

  onClick = async (event) => {
    const button = event.target.closest('[data-oasis-add]');
    if (!button || button.disabled) return;

    const label = button.textContent;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    button.textContent = S.adding;

    try {
      const res = await fetch(CFG.routes.cartAdd, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(button.dataset.oasisAdd), quantity: 1 }] }),
      });
      if (!res.ok) throw new Error('add failed');
      button.textContent = S.added;
      const cart = await refreshCart();
      if (cart) {
        // Let the theme refresh the drawer, cart icon and cart page the normal way.
        document.dispatchEvent(
          new CartAddEvent(cart, 'oasis-tier-recs', {
            source: 'oasis-tier-recs',
            itemCount: cart.item_count,
            variantId: String(button.dataset.oasisAdd),
          })
        );
      }
    } catch (_) {
      button.textContent = label;
      button.disabled = false;
      button.removeAttribute('aria-busy');
      button.classList.add('is-error');
      setTimeout(() => button.classList.remove('is-error'), 600);
    }
  };
}

/* ------------------------------------------------------------------ */
/* Win back prompt: returning visitor, exit intent, tab return         */
/* ------------------------------------------------------------------ */

const Prompt = {
  el: null,
  kind: null,

  canShow() {
    if (CFG.designMode || CFG.template === 'cart') return false;
    if (session('oasis_prompted')) return false;
    const memory = readMemory();
    if (memory.dismissedAt && Date.now() - memory.dismissedAt < DISMISS_COOLDOWN_MS) return false;
    if (document.querySelector('dialog[open]')) return false; // cart drawer, search, quick add
    return true;
  },

  content(kind) {
    const n = store.count;
    const c = capped(n);
    const viewed = readMemory().viewed || [];

    if (n > 0) {
      const primary = c >= 3 ? { label: S.cta_checkout, action: 'checkout' } : { label: S.cta_cart, action: 'cart' };
      if (kind === 'exit') return { title: S.exit_title, body: S[`exit_${c}`], primary, progress: c };
      return { title: S.back_cart_title, body: S[`back_cart_${c}`], primary, progress: c };
    }

    if (!viewed.length) return null;
    const onProduct = !!document.querySelector('oasis-tier-offer');
    const other = viewed.find((p) => !CFG.product || p.id !== CFG.product.id) || viewed[0];
    return {
      title: kind === 'exit' ? S.exit_title : S.back_view_title,
      body: S.back_view_body,
      primary: onProduct ? { label: S.cta_continue, action: 'offer' } : { label: S.cta_continue, action: 'link', href: other.url },
      viewed: viewed.slice(0, 3),
    };
  },

  show(kind) {
    if (this.el || !this.canShow()) return;
    const data = this.content(kind);
    if (!data) return;

    session('oasis_prompted', '1');
    this.kind = kind;

    const el = document.createElement('div');
    el.className = 'oasis-prompt color-scheme-1';
    el.setAttribute('role', 'dialog');
    el.setAttribute('aria-modal', 'false');
    el.setAttribute('aria-labelledby', 'OasisPromptTitle');
    el.setAttribute('aria-describedby', 'OasisPromptBody');
    el.innerHTML = this.markup(data);
    document.body.appendChild(el);
    this.el = el;

    el.addEventListener('click', (event) => {
      const action = event.target.closest('[data-oasis-action]');
      if (action) this.act(action.dataset.oasisAction, action);
    });
    this.onKey = (event) => {
      if (event.key === 'Escape') this.close(true);
    };
    document.addEventListener('keydown', this.onKey);

    requestAnimationFrame(() => requestAnimationFrame(() => el.classList.add('is-open')));
  },

  markup(data) {
    const progress =
      data.progress != null
        ? `<div class="oasis-prompt__mini" aria-hidden="true">
            <span class="oasis-prompt__seg${data.progress >= 1 ? ' is-on' : ''}"></span>
            <span class="oasis-prompt__seg${data.progress >= 2 ? ' is-on' : ''}"><em>${escapeHTML(S.stop_5)}</em></span>
            <span class="oasis-prompt__seg${data.progress >= 3 ? ' is-on' : ''}"><em>${escapeHTML(S.stop_10)}</em></span>
          </div>`
        : '';
    const viewed = data.viewed?.length
      ? `<div class="oasis-prompt__viewed">
          <p class="oasis-prompt__viewed-label">${escapeHTML(S.viewed_label)}</p>
          <ul role="list">${data.viewed
            .map(
              (p) => `<li><a href="${escapeHTML(p.url)}" title="${escapeHTML(p.title)}">
                ${p.image ? `<img src="${escapeHTML(sizedImage(p.image, 160))}" alt="${escapeHTML(p.title)}" width="56" height="56" loading="lazy">` : escapeHTML(p.title)}
              </a></li>`
            )
            .join('')}</ul>
        </div>`
      : '';
    const href = data.primary.href ? ` data-href="${escapeHTML(data.primary.href)}"` : '';

    return `<button type="button" class="oasis-prompt__close" data-oasis-action="close" aria-label="${escapeHTML(S.close)}">
        <svg viewBox="0 0 16 16" width="14" height="14" fill="none" aria-hidden="true"><path d="m4 4 8 8M12 4l-8 8" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>
      </button>
      <span class="oasis-prompt__badge" aria-hidden="true">
        <svg viewBox="0 0 20 20" width="18" height="18" fill="none"><path d="M10.6 2.5H16a1.5 1.5 0 0 1 1.5 1.5v5.4a1.5 1.5 0 0 1-.44 1.06l-6.6 6.6a1.5 1.5 0 0 1-2.12 0L3 11.72a1.5 1.5 0 0 1 0-2.12l6.6-6.6a1.5 1.5 0 0 1 1-.5Z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/><circle cx="13.5" cy="6.5" r="1.25" fill="currentColor"/></svg>
      </span>
      <h2 class="oasis-prompt__title" id="OasisPromptTitle">${escapeHTML(data.title)}</h2>
      <p class="oasis-prompt__body" id="OasisPromptBody">${escapeHTML(data.body)}</p>
      ${progress}
      ${viewed}
      <div class="oasis-prompt__actions">
        <button type="button" class="button oasis-prompt__primary" data-oasis-action="${data.primary.action}"${href}>${escapeHTML(data.primary.label)}</button>
        <button type="button" class="oasis-prompt__secondary" data-oasis-action="dismiss">${escapeHTML(S.dismiss)}</button>
      </div>`;
  },

  act(action, button) {
    switch (action) {
      case 'cart': {
        this.close(false);
        const drawer = document.querySelector('cart-drawer-component');
        if (drawer && typeof drawer.open === 'function') drawer.open();
        else window.location.href = CFG.routes.cart;
        break;
      }
      case 'checkout':
        this.close(false);
        window.location.href = `/checkout${CFG.locale ? `?locale=${encodeURIComponent(CFG.locale)}` : ''}`;
        break;
      case 'link':
        this.close(false);
        window.location.href = button.dataset.href;
        break;
      case 'offer':
        this.close(false);
        document.querySelector('oasis-tier-offer')?.highlight();
        break;
      case 'dismiss':
      case 'close':
        this.close(true);
        break;
    }
  },

  close(dismissed) {
    if (!this.el) return;
    if (dismissed) {
      const memory = readMemory();
      memory.dismissedAt = Date.now();
      writeMemory(memory);
    }
    const el = this.el;
    this.el = null;
    document.removeEventListener('keydown', this.onKey);
    el.classList.remove('is-open');
    const remove = () => el.remove();
    if (reducedMotion()) remove();
    else {
      el.addEventListener('transitionend', remove, { once: true });
      setTimeout(remove, 500);
    }
  },

  /** Keep an open prompt honest when the cart changes underneath it. */
  refresh() {
    if (!this.el) return;
    const data = this.content(this.kind);
    if (!data) return this.close(false);
    setText(this.el.querySelector('.oasis-prompt__title'), data.title);
    setText(this.el.querySelector('.oasis-prompt__body'), data.body);
    this.el.querySelectorAll('.oasis-prompt__seg').forEach((seg, i) => {
      seg.classList.toggle('is-on', (data.progress ?? 0) > i);
    });
    const primary = this.el.querySelector('.oasis-prompt__primary');
    if (primary) {
      primary.textContent = data.primary.label;
      primary.dataset.oasisAction = data.primary.action;
    }
  },
};

function setupPrompts() {
  const now = Date.now();
  const memory = readMemory();
  const newSession = !session('oasis_session');
  session('oasis_session', '1');
  const returning = newSession && memory.lastSeen && now - memory.lastSeen > RETURN_GAP_MS;

  // Remember product views (last 6) so returning visitors see what they looked at.
  if (CFG.product) {
    memory.viewed = [CFG.product, ...(memory.viewed || []).filter((p) => p.id !== CFG.product.id)].slice(0, 6);
  }
  memory.lastSeen = now;
  writeMemory(memory);

  if (CFG.designMode || CFG.template === 'cart') return;

  // 1. Returning visitor (any device): a calm welcome back a few seconds in.
  if (returning) setTimeout(() => Prompt.show('return'), 3500);

  // 2. Exit intent (desktop with a mouse): cursor leaves through the top edge.
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  if (finePointer) {
    const armedAt = now + 8000;
    const onOut = (event) => {
      if (event.relatedTarget || event.clientY > 0 || Date.now() < armedAt) return;
      Prompt.show('exit');
      if (session('oasis_prompted')) document.removeEventListener('mouseout', onOut);
    };
    document.addEventListener('mouseout', onOut);
  }

  // 3. Mobile has no exit intent; the honest equivalent is coming back to the tab
  //    after switching away. Only nudge if they already have items in the cart.
  if (!finePointer) {
    let hiddenAt = 0;
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') hiddenAt = Date.now();
      else if (hiddenAt && Date.now() - hiddenAt > TAB_AWAY_MS && store.count > 0) {
        scheduleRefresh(0);
        setTimeout(() => Prompt.show('return'), 900);
      }
    });
  }

  // Never compete with the cart drawer.
  document.addEventListener(ThemeEvents.cartUpdate, () => Prompt.el && Prompt.close(false));
}

/* ------------------------------------------------------------------ */
/* Boot                                                                */
/* ------------------------------------------------------------------ */

/**
 * The "Complete your bundle" strip lives at the end of the drawer's item list.
 * It is mounted from here (no edit to the drawer markup), and re-mounted
 * whenever the theme re-renders the drawer after a cart change.
 */
function mountDrawerRecs() {
  const drawer = document.querySelector('cart-drawer-component');
  if (!drawer) return;

  const mount = () => {
    const list = drawer.querySelector('.cart-drawer__items');
    if (!list || list.querySelector(':scope > oasis-tier-recs')) return;
    const recs = document.createElement('oasis-tier-recs');
    recs.className = 'oasis-recs';
    recs.hidden = true;
    list.appendChild(recs);
  };

  mount();
  new MutationObserver(mount).observe(drawer, { childList: true, subtree: true });
}

function init() {
  if (!customElements.get('oasis-tier-progress')) customElements.define('oasis-tier-progress', OasisTierProgress);
  if (!customElements.get('oasis-tier-offer')) customElements.define('oasis-tier-offer', OasisTierOffer);
  if (!customElements.get('oasis-tier-recs')) customElements.define('oasis-tier-recs', OasisTierRecs);
  mountDrawerRecs();

  document.addEventListener(ThemeEvents.cartUpdate, (event) => {
    if (event.detail?.data?.didError) return;
    scheduleRefresh();
  });
  document.addEventListener(ThemeEvents.discountUpdate, () => scheduleRefresh());
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) scheduleRefresh(0);
  });

  // One fetch on load gives us discount amounts and line items for the recs strip.
  refreshCart();
  setupPrompts();
}

if (CFG && !window.__oasisTierLoaded) {
  window.__oasisTierLoaded = true;
  init();
}
